import { forwardRef } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { iconButtonVariantPropType, sizePropType } from "../../../propTypes";

/**
 * Icon-only button component for actions like close, remove, edit
 * Optimized for accessibility with required aria-label
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.icon - Icon element or component
 * @param {string} props.ariaLabel - Required accessibility label
 * @param {('sm'|'md'|'lg')} [props.size='md'] - Button size
 * @param {('default'|'danger'|'ghost')} [props.variant='default'] - Visual style variant
 * @param {boolean} [props.disabled=false] - Whether button is disabled
 * @param {string} [props.className] - Additional CSS classes
 * @param {Function} [props.onClick] - Click handler
 */
const IconButton = forwardRef(
  ({ icon, ariaLabel, size = "md", variant = "default", disabled = false, className, onClick, ...rest }, ref) => {
    const buttonClasses = clsx(
      "icon-btn",
      {
        "icon-btn-sm": size === "sm",
        "icon-btn-lg": size === "lg",
        "icon-btn-danger": variant === "danger",
        "icon-btn-ghost": variant === "ghost",
        "icon-btn-disabled": disabled,
      },
      className
    );

    return (
      <button
        ref={ref}
        type="button"
        className={buttonClasses}
        disabled={disabled}
        onClick={onClick}
        aria-label={ariaLabel}
        {...rest}
      >
        {icon}
      </button>
    );
  }
);

IconButton.displayName = "IconButton";

IconButton.propTypes = {
  icon: PropTypes.node.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  size: sizePropType,
  variant: iconButtonVariantPropType,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func,
};

export default IconButton;
